import { el, requests } from "./dom";
import { icon } from "./icons";
import { leaving, openDialog } from "./modal";

// Unsaved changes (brief 1E-23, mockup v83 (1)): leaving a form with unsaved typing asks "Save
// your changes?" with Save, Discard and Stay. Every form is covered the same way: a modal (its
// Close, Esc or the scrim), a form on the page (its card), and anything marked with guardForm.
// Leaving is a link, a menu item, a tab, the back button or closing a sheet; closing the browser
// tab gets the browser's own leave question. Save presses the form's own Save and stays if it's
// refused; Discard puts every field back as it was, so nothing typed is kept; Stay goes back.
// A form that saves by itself (autosave) never asks. The rules are in createTracker (no page
// needed, tests/rules/unsaved.test.ts); startUnsaved wires them to the page.

/** A field as the tracker sees it: what it says now, and whether it's still on the page. */
export interface TrackedField {
  read(): string;
  write(value: string): void;
  connected(): boolean;
}

export interface Tracker<S> {
  /** The person is about to type in a field: what it said before is remembered once. */
  touch(field: TrackedField, scope: S): void;
  /** The forms with unsaved typing, and their changed fields. */
  dirty(): Map<S, TrackedField[]>;
  /** Saved: what the fields say now is what they started with. */
  saved(scope: S): void;
  /** Discard: every changed field goes back to what it said; nothing typed is kept. */
  discard(scope: S): void;
}

/** The rules of unsaved changes, without a page. */
export function createTracker<S>(): Tracker<S> {
  const seen = new Map<TrackedField, { was: string; scope: S }>();
  const prune = () => {
    for (const f of [...seen.keys()]) if (!f.connected()) seen.delete(f);
  };
  return {
    touch(field, scope) {
      if (!seen.has(field)) seen.set(field, { was: field.read(), scope });
    },
    dirty() {
      prune();
      const out = new Map<S, TrackedField[]>();
      for (const [f, s] of seen)
        if (f.read() !== s.was) out.set(s.scope, [...(out.get(s.scope) ?? []), f]);
      return out;
    },
    saved(scope) {
      for (const [f, s] of seen) if (s.scope === scope) s.was = f.read();
    },
    discard(scope) {
      for (const [f, s] of seen) if (s.scope === scope && f.read() !== s.was) f.write(s.was);
    },
  };
}

// ---- On the page ------------------------------------------------------------------------------------

type Field = HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement;
const SKIP_TYPES = new Set(["file", "search", "hidden", "button", "submit", "reset", "image"]);

const tracker = createTracker<HTMLElement>();
const handles = new WeakMap<Field, TrackedField>();
const saves = new WeakMap<HTMLElement, () => Promise<boolean>>();
let lastClick: { at: number; target: Element } | null = null;
let asking = false;
let passing = false;
let leavingNow = false;

const isField = (t: EventTarget | null): t is Field =>
  (t instanceof HTMLInputElement && !SKIP_TYPES.has(t.type)) ||
  t instanceof HTMLTextAreaElement ||
  t instanceof HTMLSelectElement;

function handleOf(f: Field): TrackedField {
  let h = handles.get(f);
  if (!h) {
    const box = f instanceof HTMLInputElement && (f.type === "checkbox" || f.type === "radio");
    h = {
      read: () => (box ? String((f as HTMLInputElement).checked) : f.value),
      write: (v) => {
        if (box) (f as HTMLInputElement).checked = v === "true";
        else f.value = v;
      },
      connected: () => f.isConnected,
    };
    handles.set(f, h);
  }
  return h;
}

/**
 * The form a field belongs to: its modal, a part of the page marked with guardForm, or the card
 * that holds a form. Null for a field that isn't in a form (a search box, a list filter) or one
 * that saves by itself.
 */
export function scopeOf(t: Element): HTMLElement | null {
  if (t.closest("[data-unsaved='off']")) return null;
  const scope =
    t.closest<HTMLElement>(".modal .dlg") ??
    t.closest<HTMLElement>("[data-guard]") ??
    (() => {
      const f = t.closest<HTMLElement>(".form, form");
      return f ? (f.closest<HTMLElement>("section.card, .card") ?? f) : null;
    })() ??
    // The field app's cards (app/phone/field.ts) are its forms.
    t.closest<HTMLElement>(".pcard");
  // A form that saves by itself shows its status line (app/ui/autosave.ts): it never asks.
  if (!scope || scope.querySelector(".autosave")) return null;
  return scope;
}

/** What the question names: the form's own name, its heading, or "This form". */
function nameOf(scope: HTMLElement): string {
  const named = scope.dataset.guard;
  const h = scope.querySelector("h2");
  const words = (named || h?.textContent || "This form").replace(/\s+/g, " ").trim();
  return words.slice(0, 60);
}

/**
 * Marks part of the page as one form, by its name, with its own save (answers whether it was
 * saved). Forms in a modal or a card are covered without it.
 */
export function guardForm(
  root: HTMLElement,
  o: { what: string; save?: () => Promise<boolean> },
): { saved: () => void } {
  root.dataset.guard = o.what;
  if (o.save) saves.set(root, o.save);
  return { saved: () => tracker.saved(root) };
}

/** A form's typing was saved some other way: it no longer asks. */
export const markSaved = (root: HTMLElement) => tracker.saved(root);

/** Forms with unsaved typing, but not the one the person is acting in. */
function dirtyBesides(at: Element | null): HTMLElement[] {
  return [...tracker.dirty().keys()].filter((s) => !at || !s.contains(at));
}

/** Whether anything on the page has unsaved typing. */
export const hasUnsaved = () => tracker.dirty().size > 0;

const tick = () => new Promise<void>((done) => setTimeout(done, 60));

/**
 * Waits for the requests under way to be answered (a field that saves itself as it's left is
 * saving while the person clicks away), up to a few seconds.
 */
async function settled() {
  for (let n = 0; n < 60 && requests.open > 0; n++) await tick();
  await tick();
}

/** The form's own Save: its primary button, never a Sign (signing is never done for you). */
function saveButtonOf(scope: HTMLElement): HTMLButtonElement | null {
  const all = [
    ...scope.querySelectorAll<HTMLButtonElement>(".actions .btn.pri:not([disabled])"),
    ...scope.querySelectorAll<HTMLButtonElement>(".btn.pri:not([disabled])"),
  ];
  return all.find((b) => !/\bSign\b/.test(b.textContent ?? "")) ?? null;
}

/** Presses a form's own Save and waits for the answer: whether its typing was saved. */
async function saveOne(scope: HTMLElement): Promise<boolean> {
  const own = saves.get(scope);
  if (own) {
    const ok = await own();
    if (ok) tracker.saved(scope);
    return ok;
  }
  const btn = saveButtonOf(scope);
  if (!btn) return false;
  lastClick = { at: Date.now(), target: btn };
  btn.click();
  // Until the requests it started are answered (a refusal leaves the form open, still changed).
  for (let n = 0; n < 400; n++) {
    await tick();
    if (requests.open === 0 && !btn.disabled) break;
  }
  return !scope.isConnected || !tracker.dirty().has(scope);
}

/**
 * Asks Save your changes? for these forms. Answers true to go on (saved or discarded), false to
 * stay.
 */
export async function askToLeave(asked: HTMLElement[]): Promise<boolean> {
  if (!asked.length) return true;
  if (asking) return false;
  asking = true;
  // A save already under way may settle it.
  await settled();
  const still = tracker.dirty();
  const scopes = asked.filter((s) => still.has(s));
  if (!scopes.length) {
    asking = false;
    return true;
  }
  return new Promise((done) => {
    const what = nameOf(scopes[0] as HTMLElement);
    const save = el("button", { type: "button", class: "btn pri" }, icon("check"), " Save");
    const discard = el("button", { type: "button", class: "btn" }, "Discard");
    const stay = el("button", { type: "button", class: "btn ghost" }, "Stay");
    const body = el(
      "div",
      { class: "dlg", "data-unsaved": "off" },
      el("div", { class: "cardhead", style: "margin:0" }, el("h2", {}, "Save your changes?")),
      el(
        "p",
        { class: "muted small", style: "margin:0" },
        `${what} has changes that aren't saved.`,
      ),
      el("div", { class: "actions" }, save, discard, stay),
    );
    let answered = false;
    const finish = (go: boolean) => {
      if (answered) return;
      answered = true;
      asking = false;
      close();
      done(go);
    };
    const close = openDialog(body, {
      label: "Save your changes?",
      className: "kit unsaved",
      stack: true,
      dismiss: () => finish(false),
    });
    save.addEventListener("click", async () => {
      save.disabled = true;
      discard.disabled = true;
      stay.disabled = true;
      // The question steps aside while the form's own Save runs (its refusal shows on the form).
      (body.closest(".scrim") as HTMLElement | null)?.setAttribute("hidden", "");
      let ok = true;
      for (const s of scopes) ok = (await saveOne(s)) && ok;
      finish(ok);
    });
    discard.addEventListener("click", () => {
      for (const s of scopes) tracker.discard(s);
      for (const s of scopes) tracker.saved(s);
      finish(true);
    });
    stay.addEventListener("click", () => finish(false));
    stay.focus();
  });
}

/** Asks for every form on the page with unsaved typing (the back button). */
export const askBeforeLeaving = () => askToLeave(dirtyBesides(null));

/** Leaving on purpose (signing out): the browser doesn't ask. */
export const leaveNow = () => {
  leavingNow = true;
};

/** What leaves the screen when it's clicked: links, menus, tabs, the steps and the crumbs. */
const LEAVES = [
  "[data-href]",
  '[role="tab"]',
  "[data-part]",
  ".crumbs button",
  ".rail button",
  ".msheet nav button",
  "a[href]:not([target]):not([download])",
].join(",");

/** Starts watching typing on the page (the shell calls it once). */
export function startUnsaved() {
  document.addEventListener(
    "focusin",
    (e) => {
      const t = e.target;
      if (!isField(t) || t.disabled || ("readOnly" in t && t.readOnly)) return;
      const scope = scopeOf(t);
      if (scope) tracker.touch(handleOf(t), scope);
    },
    true,
  );
  document.addEventListener(
    "pointerdown",
    (e) => {
      if (e.target instanceof Element) lastClick = { at: Date.now(), target: e.target };
    },
    true,
  );
  // A change the server took clears the form it was saved from.
  requests.saved.push(() => {
    const at = lastClick && Date.now() - lastClick.at < 15_000 ? lastClick.target : null;
    const active = document.activeElement;
    for (const s of tracker.dirty().keys())
      if ((at && s.contains(at)) || (active && active !== document.body && s.contains(active)))
        tracker.saved(s);
  });
  document.addEventListener(
    "click",
    (e) => {
      if (passing || !(e.target instanceof Element)) return;
      const n = e.target.closest<HTMLElement>(LEAVES);
      // Tabs and links inside a modal stay in it.
      if (!n || n.closest(".modal .dlg")) return;
      const scopes = dirtyBesides(n);
      if (!scopes.length) return;
      e.preventDefault();
      e.stopImmediatePropagation();
      void askToLeave(scopes).then((go) => {
        if (!go) return;
        passing = true;
        try {
          n.click();
        } finally {
          passing = false;
        }
      });
    },
    true,
  );
  window.addEventListener("beforeunload", (e) => {
    if (leavingNow || !hasUnsaved()) return;
    e.preventDefault();
    e.returnValue = "";
  });
  leaving.ask = (root) => askToLeave(tracker.dirty().has(root) ? [root] : []);
}
